import { Button, HStack, IconButton, Menu, MenuButton, MenuItem, MenuList, Text } from "@chakra-ui/react";
import { CaretDown, CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useSearchParams } from "react-router-dom";

// Cross-scope navigation control for the drill-down pages. Lets the user jump
// straight from one scope's detail page to another without going back to the
// Footprint. A prev/next arrow pair flanks a dropdown (styled to match the
// "Jump to category" control on the Scope 3 page): arrows step through the
// scopes in order and wrap around; the dropdown jumps to any scope directly.
// Navigation sets ?detailView= on the current URL (clearing ?category=, which
// only applies to the Scope 3 category page), preserving year / quarter /
// dimension params.
//
// `current` is the detailView of the page it's rendered on — used to label the
// button, resolve the prev/next targets, and omit self from the dropdown.
const SCOPES = [
  { detailView: "scopeOne", label: "Scope 1" },
  { detailView: "scopeTwo", label: "Scope 2" },
  { detailView: "scopeThree", label: "Scope 3" },
];

const ARROW_BTN_PROPS = {
  w: "28px",
  h: "28px",
  minW: "28px",
  p: 0,
  bg: "transparent",
  color: "gray.600",
  border: "1px solid",
  borderColor: "gray.200",
  borderRadius: "full",
  _hover: { bg: "gray.50", borderColor: "gray.300" },
  _focus: { boxShadow: "none" },
};

const JumpToScope = ({ current }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const currentIdx = SCOPES.findIndex((s) => s.detailView === current);
  // Wrap-around prev/next so the user never hits a dead end.
  const prev = SCOPES[(currentIdx - 1 + SCOPES.length) % SCOPES.length];
  const next = SCOPES[(currentIdx + 1) % SCOPES.length];
  const currentLabel = SCOPES[currentIdx]?.label || "scope";
  const others = SCOPES.filter((s) => s.detailView !== current);

  const go = (detailView) => {
    const params = new URLSearchParams(searchParams);
    params.set("detailView", detailView);
    params.delete("category");
    setSearchParams(params);
  };

  return (
    <HStack spacing={2}>
      <IconButton
        aria-label={`Go to ${prev.label}`}
        icon={<CaretLeft size={12} />}
        {...ARROW_BTN_PROPS}
        onClick={() => go(prev.detailView)}
      />
      <Menu>
        <MenuButton
          as={Button}
          size="sm"
          borderRadius="full"
          border="1px solid"
          borderColor="gray.300"
          bg="transparent"
          color="gray.600"
          fontWeight="normal"
          rightIcon={<CaretDown size={14} />}
          _hover={{ bg: "gray.50", borderColor: "gray.400" }}
        >
          {currentLabel}
        </MenuButton>
        <MenuList borderRadius="lg" border="1px solid" borderColor="gray.200" boxShadow="md" py={1} zIndex={2}>
          {others.map((s) => (
            <MenuItem key={s.detailView} fontSize="sm" color="gray.600" py={1.5} onClick={() => go(s.detailView)}>
              <Text>{s.label}</Text>
            </MenuItem>
          ))}
        </MenuList>
      </Menu>
      <IconButton
        aria-label={`Go to ${next.label}`}
        icon={<CaretRight size={12} />}
        {...ARROW_BTN_PROPS}
        onClick={() => go(next.detailView)}
      />
    </HStack>
  );
};

export default JumpToScope;
